// Drill-down for a clicked lane metric. Opens as a Drawer (state stays a single
// nullable "request" object — the house convention, see EscalationDrawer / IssueDrawer).
// Both tables are REAL data now (listControlTowerOrders / listControlTowerShipments).
//
// Column choices mirror the OMS PO tab (src/pages/oms/po/) and its reusable pieces
// (StatusTag for PO status, DeliveryStatusTag for shipment status, getPIOrderStatusLabel for
// Estimate status) so this reads as the same vocabulary ops already knows from that tab.
import { Drawer, Pagination, Table, Tabs, Tag, Tooltip } from "antd";
import type { ColumnsType } from "antd/es/table";
import React, { useEffect, useState } from "react";
import StatusTag from "../../../oms/components/status_tag";
import { DeliveryStatusTag } from "../../../oms/logistics/components/logistics_status_tag";
import { getPIOrderStatusLabel } from "../../../../helpers/misc_helper";
import FlagPills from "./flag_pills";
import {
  ControlTowerBoardLane,
  ControlTowerOrderRow,
  ControlTowerShipmentRow,
  ControlTowerSlaState,
  ControlTowerStatus,
  ControlTowerStatusCounts,
  listControlTowerOrders,
  listControlTowerShipments,
} from "../control_tower.api";
import { DrilldownRequest, FirstMileWarehouseKey } from "../types";
import { FIRST_MILE_DOCK_LABEL } from "../first_mile_meta";
import { LANE_GROUPS, LANE_LABEL } from "../lane_meta";

interface Props {
  request: DrilldownRequest | null;
  // The picked window. NOT named "delivery": this drawer opens on BOTH boards and each scopes on a
  // different date axis (delivery date on last mile, dispatch date on first), so the same range is
  // sent under both names and the backend reads whichever its board_lane uses.
  dateFrom: string;
  dateTo: string;
  assignedTo: string | undefined;
  boardLane: ControlTowerBoardLane; // scopes the list to first/last mile, same as the summary
  onClose: () => void;
}

// Everything that is not yet delivered — what the Team Metric table's Undelivered column counts.
const UNDELIVERED_BOARDS: ControlTowerStatus[] = ["needs_booking", "booked_but_not_picked", "picked"];

const METRIC_TITLE: Record<DrilldownRequest["metric"], string> = {
  total_orders: "Total Orders",
  total_shipments: "Total Shipments",
  total_at_risk: "At Risk",
  total_undelivered: "Undelivered",
  total_breached: "Breached",
};

// Status tabs. "all" is the client-side catch-all (no backend status); the rest match the
// `status` field the backend sends on every row. Order here is the tab order.
type StatusTab = "all" | ControlTowerStatus;
const STATUS_TABS: { key: StatusTab; label: string }[] = [
  { key: "all", label: "All" },
  { key: "needs_booking", label: "Needs Booking" },
  { key: "booked_but_not_picked", label: "Booked But Not Dispatched" },
  { key: "picked", label: "Dispatched" },
  { key: "delivered", label: "Delivered" },
];

// Which group ("Standard" / "Express" / "Outstation") a lane belongs to — shown in the drawer
// title alongside the city/party label, since the lane label alone ("Bengaluru") is ambiguous
// between Standard and Express.
const GROUP_OF = Object.fromEntries(
  LANE_GROUPS.flatMap((g) => g.lanes.map((l) => [l, g.title]))
) as Record<string, string>;

const dash = <span style={{ color: "#bbb" }}>—</span>;

// Lead ID links to the OMS order page (new tab) when the row carries an oms_sales_order_id.
const OMS_ORDER_URL = "https://procurement.materialdepot.in/oms";
const renderLeadId = (v: string | null, r: { oms_sales_order_id?: number | null }) => {
  if (!v) return dash;
  return r.oms_sales_order_id ? (
    <a href={`${OMS_ORDER_URL}/${r.oms_sales_order_id}`} target="_blank" rel="noreferrer">
      {v}
    </a>
  ) : (
    <>{v}</>
  );
};

const fmtDate = (v: string | null) =>
  v ? new Date(v).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "—";

// PO numbers stacked one per line rather than a comma-joined run-on string, with the row's
// Express / Unloading flags as pills below — used by both the order rows (all POs raised against an
// order) and the shipment rows (POs consolidated on a truck).
const renderPoNumbers = (
  v: string[] | null | undefined,
  r: { express?: boolean; unloading?: boolean }
) => {
  const pos = v ?? [];
  if (!pos.length && !r.express && !r.unloading) return dash;
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
      {pos.map((po) => (
        <span key={po}>{po}</span>
      ))}
      <FlagPills express={r.express} unloading={r.unloading} />
    </div>
  );
};

// SLA state, off the row's `sla_state` key. Null (not yet evaluable) falls through to a dash.
const SLA_LABEL: Record<"breached" | "at_risk" | "on_time", { text: string; color: string }> = {
  breached: { text: "Breached", color: "red" },
  at_risk: { text: "At Risk", color: "orange" },
  on_time: { text: "On Time", color: "green" },
};
const slaColumn = {
  title: "SLA",
  dataIndex: "sla_state",
  key: "sla_state",
  render: (
    v: ControlTowerSlaState,
    r: { breach_reason?: string | null; risk_reason?: string | null }
  ) => {
    const m = v ? SLA_LABEL[v] : undefined;
    if (!m) return dash;
    const tag = <Tag color={m.color}>{m.text}</Tag>;
    // On a breached / at-risk pill, surface why on hover.
    const reason =
      v === "breached" ? r.breach_reason : v === "at_risk" ? r.risk_reason : null;
    return reason ? (
      <Tooltip title={reason} overlayStyle={{ maxWidth: 480 }}>
        <span style={{ cursor: "help" }}>{tag}</span>
      </Tooltip>
    ) : (
      tag
    );
  },
};

const orderColumns: ColumnsType<ControlTowerOrderRow> = [
  { title: "Lead ID", dataIndex: "lead_id", key: "lead_id", render: renderLeadId },
  { title: "PO Numbers", dataIndex: "po_numbers", key: "po_numbers", render: renderPoNumbers },
  {
    title: "Estimate Status",
    dataIndex: "estimate_status",
    key: "estimate_status",
    render: (v) => getPIOrderStatusLabel(v),
  },
  {
    title: "PO Status",
    dataIndex: "po_status",
    key: "po_status",
    // On an "error" pill, StatusTag surfaces `error_message` on hover (it only shows the tooltip
    // for error/held statuses, so passing it unconditionally is safe).
    render: (v, record) => <StatusTag status={v} tooltip={record.error_message} />,
  },
  {
    title: "Target Delivery",
    dataIndex: "target_delivery_date",
    key: "target_delivery_date",
    render: fmtDate,
  },
  slaColumn,
  { title: "Assigned To", dataIndex: "assigned_to", key: "assigned_to", render: (v) => v ?? dash },
];

// WHERE THE GOODS COME FROM. Named "From", not "Vendor": a consolidated truck and our own stock
// both leave OUR warehouse, and only a single dropship PO leaves the vendor's — one column, read
// off the leg's own origin, answers all three.
const fromColumn: ColumnsType<ControlTowerShipmentRow>[number] = {
  title: "From",
  dataIndex: "origin_label",
  key: "origin_label",
  render: (v: string | null) => v ?? dash,
};

// The FIRST MILE column set — the same shape as the GRN list on the First Mile tab, so an inbound
// reads the same wherever it is opened from. A last-mile vocabulary (Estimate Status / Target
// Delivery / Shipment Status) says little about a truck coming INTO our warehouse.
const firstMileShipmentColumns: ColumnsType<ControlTowerShipmentRow> = [
  { title: "PO Number", dataIndex: "po_numbers", key: "po_numbers", render: renderPoNumbers },
  { title: "Lead ID", dataIndex: "lead_id", key: "lead_id", render: renderLeadId },
  { title: "Vendor", dataIndex: "supplier", key: "supplier", render: (v: string | null) => v ?? dash },
  {
    title: "Warehouse",
    dataIndex: "dock",
    key: "dock",
    render: (v: FirstMileWarehouseKey | null) => (v ? FIRST_MILE_DOCK_LABEL[v] ?? v : dash),
  },
  { title: "When", dataIndex: "dispatch_date", key: "dispatch_date", render: fmtDate },
  {
    title: "Weight",
    dataIndex: "cargo_weight_kg",
    key: "cargo_weight_kg",
    render: (v: number | null) => (v === null || v === undefined ? dash : `${v} kg`),
  },
  {
    title: "GRN",
    dataIndex: "grn_marked",
    key: "grn_marked",
    render: (v: boolean | null) =>
      v === null || v === undefined ? (
        dash
      ) : v ? (
        <Tag color="green">Marked</Tag>
      ) : (
        <Tag color="gold">Awaiting</Tag>
      ),
  },
  {
    title: "Shipment Status",
    dataIndex: "shipment_status",
    key: "shipment_status",
    render: (v) => <DeliveryStatusTag status={v} />,
  },
];

const shipmentColumns: ColumnsType<ControlTowerShipmentRow> = [
  { title: "PO Numbers", dataIndex: "po_numbers", key: "po_numbers", render: renderPoNumbers },
  { title: "Lead ID", dataIndex: "lead_id", key: "lead_id", render: renderLeadId },
  fromColumn,
  {
    title: "Estimate Status",
    dataIndex: "estimate_status",
    key: "estimate_status",
    render: (v) => getPIOrderStatusLabel(v),
  },
  {
    title: "Target Delivery Date",
    dataIndex: "delivery_date",
    key: "delivery_date",
    render: fmtDate,
  },
  {
    title: "Shipment Status",
    dataIndex: "shipment_status",
    key: "shipment_status",
    render: (v) => <DeliveryStatusTag status={v} />,
  },
  slaColumn,
  { title: "Assigned To", dataIndex: "assigned_to", key: "assigned_to", render: (v) => v ?? dash },
];

const ShipmentListDrawer: React.FC<Props> = ({
  request,
  dateFrom,
  dateTo,
  assignedTo,
  boardLane,
  onClose,
}) => {
  const isOrdersView = request?.metric === "total_orders";

  const [orderRows, setOrderRows] = useState<ControlTowerOrderRow[]>([]);
  const [shipmentRows, setShipmentRows] = useState<ControlTowerShipmentRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState<StatusTab>("all");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [total, setTotal] = useState(0); // whole-set row count, for the pager
  const [statusCounts, setStatusCounts] = useState<ControlTowerStatusCounts>({}); // for the tab badges

  // A new drill-down (or a scope change) resets back to page 1 and the "All" tab. Kept separate
  // from the fetch effect below so paging within the same drill-down doesn't reset itself.
  useEffect(() => {
    setPage(1);
    setActiveTab("all");
  }, [isOrdersView, request, dateFrom, dateTo, assignedTo]);


  useEffect(() => {
    if (!request) return;
    let cancelled = false;
    setLoading(true);
    // "all" (the overview strip) omits `lane` entirely — the backend returns every lane in one
    // response. A lane card asks for just its own lane. Either way it's a single request.
    const lane = request.lane === "all" ? undefined : request.lane;
    // The active tab filters server-side: "all" sends no board, any other tab sends its board
    // so the backend returns (and counts) only those rows.
    // "Undelivered" is the whole board minus the delivered bucket — sent as the multi-select the
    // backend already accepts, so `count` and the tab badges stay server-side truth.
    const board =
      activeTab !== "all"
        ? activeTab
        : request.metric === "total_undelivered"
        ? UNDELIVERED_BOARDS.join(",")
        : undefined;
    // "At Risk" / "Breached" are the Shipments list with an extra SLA filter — else it's omitted.
    const sla_state =
      request.metric === "total_at_risk"
        ? ("at_risk" as const)
        : request.metric === "total_breached"
        ? ("breached" as const)
        : undefined;
    const params = {
      // Both axes, every time — first_mile reads dispatch_*, last_mile delivery_*, and an omitted
      // bound defaults to TODAY server-side (which is how this silently returned an empty
      // first-mile list before).
      delivery_from: dateFrom,
      delivery_to: dateTo,
      dispatch_from: dateFrom,
      dispatch_to: dateTo,
      // A Team Metric row drills into ONE person's work, whatever the page filter says.
      assigned_to: request.owner?.id ?? assignedTo,
      board_lane: boardLane,
      lane,
      board,
      sla_state,
      page,
      page_size: pageSize,
    };

    if (isOrdersView) {
      listControlTowerOrders(params).then((res) => {
        if (cancelled) return;
        setOrderRows(res?.results ?? []);
        setTotal(res?.count ?? 0);
        setStatusCounts(res?.board_counts ?? {});
        setLoading(false);
      });
    } else {
      listControlTowerShipments(params).then((res) => {
        if (cancelled) return;
        setShipmentRows(res?.results ?? []);
        setTotal(res?.count ?? 0);
        setStatusCounts(res?.board_counts ?? {});
        setLoading(false);
      });
    }
    return () => {
      cancelled = true;
    };
  }, [isOrdersView, request, dateFrom, dateTo, assignedTo, boardLane, activeTab, page, pageSize]);

  const title = request
    ? request.owner
      ? `${request.owner.name} — ${METRIC_TITLE[request.metric]}`
      : request.lane === "all"
      ? `All lanes — ${METRIC_TITLE[request.metric]}`
      : `${GROUP_OF[request.lane]} · ${LANE_LABEL[request.lane]} — ${METRIC_TITLE[request.metric]}`
    : "";

  // The backend already filtered to the active tab's status, so this page is rendered as-is —
  // no client-side filtering. `total` is scoped to the active status, `status_counts` (below) is
  // always the whole-set breakdown so every tab badge stays right.
  const pageRows = (isOrdersView ? orderRows : shipmentRows) as (ControlTowerOrderRow | ControlTowerShipmentRow)[];

  // Board tabs are hidden entirely for "Breached", and drop "Delivered" for "At Risk" — a
  // delivered shipment can be neither breached nor at risk, so that tab would always be empty.
  const isBreached = request?.metric === "total_breached";
  const isAtRisk = request?.metric === "total_at_risk";
  // "Delivered" is dropped for At Risk (a delivered shipment can be neither) and for Undelivered
  // (where it is the one bucket the metric excludes by definition).
  const isUndelivered = request?.metric === "total_undelivered";
  const visibleTabs =
    isAtRisk || isUndelivered ? STATUS_TABS.filter((t) => t.key !== "delivered") : STATUS_TABS;

  // "All" badge = every status summed (status_counts is always the whole-set breakdown). `total`
  // can't be used here since it's scoped to the active status once a status tab is selected.
  const allTotal = Object.values(statusCounts).reduce((sum, n) => sum + (n ?? 0), 0);
  const tabItems = visibleTabs.map((t) => ({
    key: t.key,
    label: `${t.label} (${t.key === "all" ? allTotal : statusCounts[t.key] ?? 0})`,
  }));

  const onTabChange = (k: string) => {
    setActiveTab(k as StatusTab);
    setPage(1); // each status starts on its own page 1
  };

  return (
    <Drawer title={title} open={!!request} onClose={onClose} width={960} destroyOnClose>
      {!isBreached && <Tabs activeKey={activeTab} onChange={onTabChange} items={tabItems} size="small" />}
      <Table
        rowKey={(r: ControlTowerOrderRow | ControlTowerShipmentRow) =>
          isOrdersView
            ? `${(r as ControlTowerOrderRow).lead_id}-${
                (r as ControlTowerOrderRow).po_numbers?.join(",") || "none"
              }`
            : (r as ControlTowerShipmentRow).id
        }
        size="small"
        loading={loading}
        // Two distinct row shapes (orders vs shipments) share this one table, switched on
        // `isOrdersView` — columns and dataSource always change together.
        columns={
          (isOrdersView
            ? orderColumns
            : boardLane === "first_mile"
            ? firstMileShipmentColumns
            : shipmentColumns) as ColumnsType<ControlTowerOrderRow>
        }
        dataSource={pageRows as ControlTowerOrderRow[]}
        // Paginated server-side (separate <Pagination> below) — the table just renders the page it
        // was handed, so its own pagination is off (house pattern, cf. CallRecordsTab).
        pagination={false}
        scroll={{ x: 900 }}
      />
      <div style={{ marginTop: 16, textAlign: "right" }}>
        <Pagination
          current={page}
          pageSize={pageSize}
          total={total}
          showSizeChanger
          showTotal={(t) => `${t} ${isOrdersView ? "orders" : "shipments"}`}
          onChange={(p, size) => {
            setPage(p);
            setPageSize(size);
          }}
        />
      </div>
    </Drawer>
  );
};

export default ShipmentListDrawer;
